const DB_NAME = "report-assistant-photo-db";
const STORE_NAME = "photos";
const REF_PREFIX = "photo-ref:";

let dbPromise: Promise<IDBDatabase> | null = null;
const photoCache = new Map<string, string>();

function openDatabase(): Promise<IDBDatabase> {
  if (dbPromise) {
    return dbPromise;
  }

  dbPromise = new Promise((resolve, reject) => {
    const request = window.indexedDB.open(DB_NAME, 1);

    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        database.createObjectStore(STORE_NAME, { keyPath: "id" });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error("IndexedDB open failed."));
  });

  return dbPromise;
}

function makeRef(): string {
  if ("randomUUID" in crypto) {
    return `${REF_PREFIX}${crypto.randomUUID()}`;
  }
  return `${REF_PREFIX}${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function isPhotoRef(value: string): boolean {
  return value.startsWith(REF_PREFIX);
}

export async function persistImageDataUrls(dataUrls: string[]): Promise<string[]> {
  if (!dataUrls.length) {
    return [];
  }

  const database = await openDatabase();
  return await new Promise<string[]>((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    const refs: string[] = [];

    for (const dataUrl of dataUrls) {
      const id = makeRef();
      refs.push(id);
      photoCache.set(id, dataUrl);
      store.put({
        id,
        dataUrl,
        createdAt: new Date().toISOString(),
      });
    }

    transaction.oncomplete = () => resolve(refs);
    transaction.onerror = () =>
      reject(transaction.error || new Error("Failed to persist photo data."));
  });
}

export async function preloadPhotoRefs(refs: string[]): Promise<void> {
  const missingRefs = refs.filter((ref) => isPhotoRef(ref) && !photoCache.has(ref));
  if (!missingRefs.length) {
    return;
  }

  const database = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, "readonly");
    const store = transaction.objectStore(STORE_NAME);
    let remaining = missingRefs.length;

    for (const ref of missingRefs) {
      const request = store.get(ref);
      request.onsuccess = () => {
        const result = request.result as { id: string; dataUrl: string } | undefined;
        if (result?.dataUrl) {
          photoCache.set(ref, result.dataUrl);
        }
        remaining -= 1;
        if (remaining === 0) {
          resolve();
        }
      };
      request.onerror = () => reject(request.error || new Error("Failed to preload photo data."));
    }
  });
}

export function resolvePhotoUrl(value: string): string {
  if (!isPhotoRef(value)) {
    return value;
  }
  return photoCache.get(value) || "";
}

export async function deletePhotoRefs(refs: string[]): Promise<void> {
  const targetRefs = refs.filter(isPhotoRef);
  if (!targetRefs.length) {
    return;
  }

  const database = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);

    for (const ref of targetRefs) {
      photoCache.delete(ref);
      store.delete(ref);
    }

    transaction.oncomplete = () => resolve();
    transaction.onerror = () =>
      reject(transaction.error || new Error("Failed to delete photo data."));
  });
}
